import 'maplibre-gl/dist/maplibre-gl.css';
import './styles/base.css';
import './styles/layout.css';
import './styles/components.css';
import './styles/map.css';
import './styles/polish.css';

import { createMap } from './map/index.js';
import { switchBasemap } from './map/basemap.js';
import { setupOverlaySources } from './map/overlaySources.js';
import { UserLayerManager } from './map/userLayers.js';

import {
  activateIsochrone, clearIsochrone, registerUserLayers as registerIsochroneUserLayers,
} from './analysis/isochrone.js';
import {
  activateBuffer, clearBuffer, registerUserLayers as registerBufferUserLayers,
} from './analysis/buffer.js';
import { activateMeasure, activateMeasureArea } from './analysis/measure.js';
import { activateDraw, activateText, clearAnnotations } from './analysis/draw.js';
import { toggleTimeSeries, playTimeSeries, setMonth, initTimeSeries } from './analysis/timeSeries.js';
import { runAIAnalysis, registerUserLayers as registerAIUserLayers } from './analysis/ai.js';
import {
  openOdFlowModal, clearOdFlow,
  registerMap as registerOdMap,
  registerUserLayers as registerOdUserLayers,
} from './analysis/odFlow.js';
import { openSuitabilityModal, registerUserLayers as registerSuitabilityUserLayers } from './analysis/suitability.js';
import {
  openEmergingHotspotModal, clearHotspot,
  registerUserLayers as registerEhotUserLayers,
  registerMap as registerEhotMap,
} from './analysis/emergingHotspot.js';

import { showSidePanel, toggleLayerItem, filterLayers } from './ui/sidebar.js';
import { backToNational, toggleRightPanel, toggleDrawer } from './ui/rightPanel.js';
import { toast } from './ui/toast.js';
import { openShareModal, closeModal, copyShareLink } from './ui/modal.js';
import { initSplash } from './ui/splash.js';

import { importCSV } from './io/csv.js';
import { exportMap } from './io/export.js';

import { auth } from './auth/state.js';
import { openAuthModal, closeAuthModal, logout } from './auth/modal.js';
import {
  initMyLayersPanel, handleMyLayerClick, refreshMyLayers
} from './ui/myLayersPanel.js';
import {
  initCatalogPanel, handleCatalogClick, refreshCatalog
} from './ui/catalogPanel.js';
import { initUploadDialog, cancelActiveUpload } from './ui/uploadDialog.js';
import {
  saveCurrentAsProject, openProjectsMenu, closeProjectsModal, maybeOpenProjectFromUrl
} from './ui/projectsMenu.js';
import { closeAnalyticsModal } from './ui/analyticsModal.js';
import { openToolInfoModal } from './ui/toolInfoModal.js';
import { closeStyleModal } from './ui/styleModal.js';
import { openAuditLogModal, closeAuditLogModal } from './ui/auditLogModal.js';
import { initDashboard, refreshDashboard } from './ui/dashboard.js';
import { initStoriesWorkspace, openStories, closeStories } from './ui/stories.js';
import { listLayers as apiListLayers } from './api/layers.js';

initSplash();

const map = createMap();
const userLayers = new UserLayerManager(map);
registerIsochroneUserLayers(userLayers);
registerBufferUserLayers(userLayers);
registerAIUserLayers(userLayers);
registerOdMap(map);
registerOdUserLayers(userLayers);
registerSuitabilityUserLayers(userLayers);
registerEhotUserLayers(userLayers);
registerEhotMap(map);

// Track which basemap is currently active so projects can save/restore it.
let currentBasemap = 'light';
function switchBasemapTracked(map, style, btn) {
  switchBasemap(map, style, btn);
  currentBasemap = style;
}

map.on('load', () => {
  // The map boots empty — users add their own layers via the upload dialog
  // (vector + raster) or by browsing the public catalog in the Layers panel.
  setupOverlaySources(map);
  initMyLayersPanel(userLayers, { onChange: () => { refreshCatalog(); refreshDashboard(); } });
  initCatalogPanel(userLayers, { onChange: () => { refreshMyLayers(); refreshDashboard(); } });
  initUploadDialog();
  initDashboard(userLayers);
  initTimeSeries(userLayers);
  initStoriesWorkspace({
    map,
    userLayers,
    switchBasemap: switchBasemapTracked,
    getCurrentBasemap: () => currentBasemap,
    getAllLayers: () => apiListLayers().catch(() => []),
  });

  // If the URL has ?project=N, open it automatically (assumes user is signed in)
  maybeOpenProjectFromUrl(map, switchBasemapTracked);
});

// Reflect auth state in the navbar
auth.onChange(updateAuthUI);
updateAuthUI(auth.getUser());

function updateAuthUI(user) {
  const signInBtn = document.getElementById('navSignIn');
  const avatar    = document.getElementById('navAvatar');
  const activity  = document.getElementById('navActivity');
  if (user) {
    signInBtn.style.display = 'none';
    avatar.style.display = 'grid';
    avatar.textContent = (user.full_name || user.email || '?').slice(0, 2).toUpperCase();
    if (activity) activity.style.display = '';
  } else {
    signInBtn.style.display = '';
    avatar.style.display = 'none';
    if (activity) activity.style.display = 'none';
  }
}

// ===== Event delegation =====

const actions = {
  // Nav / modals
  openShareModal,
  closeModal,
  copyShareLink,
  exportMap:        () => exportMap(map),
  toggleRightPanel,

  // Auth
  openSignIn:       () => openAuthModal('login'),
  closeAuthModal,
  signOut:          () => { logout(); refreshMyLayers(); },

  // Projects
  saveProject:      () => saveCurrentAsProject(map, currentBasemap),
  openProjects:     () => openProjectsMenu(map, switchBasemapTracked),
  closeProjectsModal,

  // Analytics modal
  closeAnalyticsModal,

  // Style modal
  closeStyleModal,

  // Audit log
  openAuditLog: openAuditLogModal,
  closeAuditLogModal,

  // Mobile drawers
  toggleSidebar:    () => toggleDrawer('sidebar'),

  // Upload
  cancelUpload:     cancelActiveUpload,

  // Stories
  openStories,
  closeStories,

  // My layers panel
  refreshMyLayers:  () => refreshMyLayers(),
  refreshCatalog:   () => refreshCatalog(),

  // Analysis
  activateIsochrone: () => activateIsochrone(map),
  clearIsochrone:    () => clearIsochrone(map),
  activateBuffer:    () => activateBuffer(map),
  clearBuffer:       () => clearBuffer(map),
  activateMeasure:     () => activateMeasure(map),
  activateMeasureArea: () => activateMeasureArea(map),
  activateDraw:        () => activateDraw(map),
  activateText:        () => activateText(map),
  clearAnnotations:    () => clearAnnotations(map),
  toggleTimeSeries:  () => toggleTimeSeries(map),
  playTimeSeries:    () => playTimeSeries(map),
  runAIAnalysis,
  openOdFlow:        () => openOdFlowModal(),
  clearOdFlow:       () => clearOdFlow(),
  openSuitability:   () => openSuitabilityModal(),
  openEmergingHotspot: () => openEmergingHotspotModal(),
  clearEmergingHotspot: () => clearHotspot(),
  showToolInfo:        (target) => openToolInfoModal(target?.dataset?.tool),

  // Right panel
  backToNational,

  // I/O
  importCSV:       () => importCSV(map),
};

document.addEventListener('click', async e => {
  // User-uploaded layer rows + their delete buttons live in #myLayersHost.
  // Handle them first so the generic .litem handler below doesn't also fire.
  if (e.target.closest('#myLayersHost')) {
    const handled = await handleMyLayerClick(e.target);
    if (handled) return;
  }

  // Catalog (public) layer rows live in #catalogHost.
  if (e.target.closest('#catalogHost')) {
    const handled = await handleCatalogClick(e.target);
    if (handled) return;
  }

  const target = e.target.closest('[data-action]');
  if (target) {
    const fn = actions[target.dataset.action];
    if (fn) { e.preventDefault(); fn(target); }
    return;
  }

  const toastEl = e.target.closest('[data-toast]');
  if (toastEl) { toast(toastEl.dataset.toast); return; }

  const sideTab = e.target.closest('[data-side-panel]');
  if (sideTab) { showSidePanel(sideTab.dataset.sidePanel, sideTab); return; }

  const layerItem = e.target.closest('.litem');
  if (layerItem) { toggleLayerItem(map, layerItem); return; }

  const basemapBtn = e.target.closest('[data-basemap]');
  if (basemapBtn) { switchBasemapTracked(map, basemapBtn.dataset.basemap, basemapBtn); return; }
});

// Style controls inside the layers panel
document.addEventListener('input', e => {
  const styleEl = e.target.closest('[data-style]');
  if (styleEl) {
    const kind = styleEl.dataset.style;
    if (kind === 'opacity') setLayerOpacity(map, styleEl.value);
    if (kind === 'radius')  setFacilityRadius(map, styleEl.value);
  }

  if (e.target.id === 'layerSearch') filterLayers(e.target.value);
  if (e.target.id === 'timeSlider')  setMonth(map, +e.target.value);
});

document.addEventListener('change', e => {
  const styleEl = e.target.closest('[data-style]');
  if (styleEl?.dataset.style === 'colorBy') colorFacilitiesBy(map, styleEl.value);
});
